/**
 * A room's size: the common sizes for its kind of space as footprint tiles,
 * or — behind "Enter your own dimensions" — length, width and wall height by
 * the number. The create wizard's Size step and the room editor's Size tab.
 */

import React from 'react';
import { LayoutGrid, Ruler } from 'lucide-react';
import { DimensionInput } from '../../ui/DimensionInput';
import { sizeTitle } from './roomSteps';
import { formatFeetInches } from '../SectionEditOverlay';
import { ROOM_LIMITS } from '../../../calculations/plan';
import { RoomSizePreset, roomSizePresets } from '../../../data/roomSizePresets';
import { DESIGNER_STRINGS as S } from '../strings';
import './createWizard.css';

const W = S.planner.wizard;

export interface RoomSize { length: number; width: number; height: number }

export const sameSize = (p: RoomSize, q: RoomSize) =>
  Math.abs(p.length - q.length) < 1e-6 && Math.abs(p.width - q.width) < 1e-6 && Math.abs(p.height - q.height) < 1e-6;

/** The common size this is, if it is one. */
export const presetOf = (spaceType: string, size: RoomSize): RoomSizePreset | undefined => roomSizePresets(spaceType).find((p) => sameSize(p, size));

/** A room's footprint on a scrap of blueprint, to a scale shared by the tiles beside it. */
const Footprint: React.FC<{ length: number; width: number; scale: number; active: boolean }> = ({ length, width, scale, active }) => {
  const w = Math.max(6, length * scale);
  const h = Math.max(6, width * scale);
  return (
    <svg width={76} height={56} viewBox="0 0 76 56" aria-hidden="true" className="cw-footprint">
      <rect x={(76 - w) / 2} y={(56 - h) / 2} width={w} height={h} rx={1.5} fill={active ? 'rgba(254,177,0,0.22)' : 'rgba(234,242,255,0.12)'} stroke={active ? '#FEB100' : '#eaf2ff'} strokeWidth={1.5} />
    </svg>
  );
};

export interface SizePickerProps {
  spaceType: string;
  size: RoomSize;
  /** A new size — a tile, or one number changed. */
  onChange: (size: RoomSize) => void;
  /** Showing the numbers instead of the tiles. */
  custom: boolean;
  onCustom: (custom: boolean) => void;
}

export const SizePicker: React.FC<SizePickerProps> = ({ spaceType, size, onChange, custom, onCustom }) => {
  const presets = roomSizePresets(spaceType);
  const chosen = presets.find((p) => sameSize(p, size));
  const scale = Math.min(64 / Math.max(...presets.map((p) => p.length)), 46 / Math.max(...presets.map((p) => p.width)));

  if (custom) {
    return (
      <>
        <div className="cw-dims">
          {([
            { key: 'length', label: S.planner.length, lim: ROOM_LIMITS.side },
            { key: 'width', label: S.planner.width, lim: ROOM_LIMITS.side },
            { key: 'height', label: W.wallHeight, lim: ROOM_LIMITS.height },
          ] as const).map((f) => (
            <label key={f.key} className="cw-label">
              {f.label}
              <span style={{ display: 'block', marginTop: 6 }}>
                <DimensionInput value={size[f.key]} mode="ftin" min={f.lim.min} max={f.lim.max} ariaLabel={f.label} onChange={(v) => onChange({ ...size, [f.key]: v })} />
              </span>
            </label>
          ))}
        </div>
        <button type="button" className="cw-custom-btn is-back" onClick={() => onCustom(false)}>
          <LayoutGrid size={16} /> {W.backToPresets}
        </button>
      </>
    );
  }

  return (
    <>
      <div className="cw-size-grid" role="radiogroup" aria-label={sizeTitle(spaceType)}>
        {presets.map((p) => {
          const active = chosen?.id === p.id;
          return (
            <button key={p.id} type="button" role="radio" aria-checked={active} className={`cw-size-tile${active ? ' is-active' : ''}`} onClick={() => onChange({ length: p.length, width: p.width, height: p.height })}>
              <Footprint length={p.length} width={p.width} scale={scale} active={active} />
              <span className="cw-size-name">{p.name}</span>
              <span className="cw-size-dims">{formatFeetInches(p.length)} × {formatFeetInches(p.width)}</span>
              <span className="cw-size-height">{W.walls(formatFeetInches(p.height))}</span>
            </button>
          );
        })}
      </div>
      <button type="button" className="cw-custom-btn" onClick={() => onCustom(true)}>
        <Ruler size={16} /> {W.custom}
        {!chosen && <span className="cw-custom-now">{formatFeetInches(size.length)} × {formatFeetInches(size.width)} · {W.walls(formatFeetInches(size.height))}</span>}
      </button>
    </>
  );
};

export default SizePicker;
